'use client'
import { useCallback, useMemo } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import type { OldProductDTO, Option, ProductPrice, UIVariant } from 'ecosystem'

export interface UseRegularVariants {
  prices: ProductPrice
  selectVariantOnUpdate: (variantGroupId: string, variants: Option<UIVariant[]>) => void
  selectVariant: (variant: UIVariant) => void
  isVariantSelected: (variant: UIVariant) => boolean
}

const useRegularVariants = (product: OldProductDTO): UseRegularVariants => {
  const router = useRouter()
  const prices = useMemo<ProductPrice>(
    () => ({
      price: product.price,
      originalPrice: product.originalPrice,
      priceWithoutVat: product.priceWithoutVat ?? null,
      originalPriceWithoutVat: product.originalPriceWithoutVat ?? null
    }),
    [product]
  )
  const pathname = usePathname()

  const selectVariant = useCallback(
    (variant: UIVariant) => {
      const path = variant.path

      if (path !== pathname) {
        router.push(path)
      }
    },
    [pathname, router]
  )

  const isVariantSelected = useCallback(
    (variant: UIVariant) => {
      return product.id === variant.id
    },
    [product.id]
  )

  const selectVariantOnUpdate = useCallback(
    (_: string, variants: Option<UIVariant[]>) => {
      if (!variants) return
      const currentIndex = variants.findIndex(isVariantSelected)
      const exists = currentIndex !== -1

      if (variants.length === 1 || (variants.length && !exists)) {
        selectVariant(variants[0])
      }
    },
    [isVariantSelected, selectVariant]
  )

  return {
    prices,
    selectVariant,
    selectVariantOnUpdate,
    isVariantSelected
  }
}

export default useRegularVariants
